import { Row } from '@tanstack/react-table';
import { isEmpty, orderBy, union } from 'lodash';

import Expander from '../components/Expander';
import { TabView } from '../components/TabsViews';

const customSort = (rowA: Row<any>, rowB: Row<any>, id: string) => {
  const valueA = rowA.getValue<string>(id);
  const valueB = rowB.getValue<string>(id);

  // Handle date values in dd/MM/yyyy format
  if (
    typeof valueA === 'string' &&
    typeof valueB === 'string' &&
    valueA.match(/^\d{2}\/\d{2}\/\d{4}$/) &&
    valueB.match(/^\d{2}\/\d{2}\/\d{4}$/)
  ) {
    // Convert dd/MM/yyyy to Date objects
    const [dayA, monthA, yearA] = valueA.split('/');
    const [dayB, monthB, yearB] = valueB.split('/');

    const dateA = new Date(
      parseInt(yearA),
      parseInt(monthA) - 1,
      parseInt(dayA),
    );
    const dateB = new Date(
      parseInt(yearB),
      parseInt(monthB) - 1,
      parseInt(dayB),
    );

    return dateB.getTime() - dateA.getTime();
  }

  // Handle percentage values
  if (
    typeof valueA === 'string' &&
    typeof valueB === 'string' &&
    (valueA.includes('%') || valueB.includes('%'))
  ) {
    const formattedA = parseFloat(valueA.replace('%', ''));
    const formattedB = parseFloat(valueB.replace('%', ''));
    return formattedB - formattedA;
  }

  // Handle numeric values
  const numA = parseFloat(valueA);
  const numB = parseFloat(valueB);
  if (!Number.isNaN(numA) && !Number.isNaN(numB)) {
    return numB - numA;
  }

  // Handle string values
  if (typeof valueA === 'string' && typeof valueB === 'string') {
    return valueB.localeCompare(valueA);
  }

  // Generic fallback
  return String(valueB).localeCompare(String(valueA));
};

function convertColumns(
  data: any[],
): {
  columns: any[];
  restColumns: { [key: string]: any };
} {
  const newData = data.reduce(
    (prev: any, curr: any, index) => {
      const {
        key,
        header,
        minWidth,
        maxWidth,
        width,
        header_tooltip,
        columnHeaderBackgroundColor,
        filter,
        type,
        attribute,
        ...rest
      } = curr;

      if (curr.expander)
        return {
          columns: [
            ...prev.columns,
            {
              accessorKey: 'expander',
              header: '+',
              minWidth,
              maxWidth,
              width,
              columnHeaderBackgroundColor,
              sortingFn: customSort,
              enableSorting: rest?.enableSorting ?? rest?.sortable ?? true,
              filterFn: 'customFilter',
              headerTooltip: '+',
              cell: ({ row }: any) => {
                return <Expander row={row} />;
              },
              attribute,
            },
          ],
          restColumns: {
            ...prev.restColumns,
            [key || 'expander']: { ...rest },
          },
        };

      if (type === 'group') {
        const restGroupedColumns = curr.columns.reduce(
          (columns, column) => ({
            ...columns,
            [column.key]: column,
          }),
          {},
        );

        return {
          columns: [
            ...prev.columns,
            {
              accessorKey: key,
              header,
              minWidth,
              maxWidth,
              width,
              columnHeaderBackgroundColor,
              headerTooltip: header_tooltip,
              sortingFn: customSort,
              enableSorting: rest?.enableSorting ?? rest?.sortable ?? true,
              filterFn: 'customFilter',
              columns: curr.columns.map((column) => ({
                ...column,
                accessorKey: column.key,
                header: column.header,
                minWidth: column.minWidth,
                maxWidth: column.maxWidth,
                width: column.width,
                columnHeaderBackgroundColor: column.columnHeaderBackgroundColor,
                headerTooltip: column.header_tooltip,
                sortingFn: customSort,
                enableSorting: rest?.enableSorting ?? rest?.sortable ?? true,
                filterFn: 'customFilter',
                attribute,
              })),
            },
          ],
          restColumns: {
            ...prev.restColumns,
            ...restGroupedColumns,
            [key]: { ...rest, flex: curr.columns.length },
          },
        };
      }

      return {
        columns: [
          ...prev.columns,
          {
            ...rest,
            accessorKey: key,
            header,
            minWidth,
            maxWidth,
            width,
            columnHeaderBackgroundColor,
            headerTooltip: header_tooltip,
            sortingFn: customSort,
            enableSorting: rest?.enableSorting ?? rest?.sortable ?? true,
            filter,
            filterFn: 'customFilter',
            attribute,
          },
        ],
        restColumns: { ...prev.restColumns, [key]: { ...rest } },
      };
    },
    {
      columns: [],
      restColumns: {},
    },
  );
  return newData;
}

const defaultCellOptions = {
  backgroundColor: 'transparent',
  color: '#000000',
  tooltipType: 'default',
  textAlign: 'center',
  type: 'default',
};

function convertItems(
  data: any[],
  columns: any[],
): { values: any[]; restValues: any[] } {
  const columnsKeys = columns.reduce((array, column) => {
    if (column?.columns && !isEmpty(column?.columns)) {
      const subColumns = column?.columns?.map((subColumn) => {
        return subColumn?.accessorKey || subColumn?.key;
      });

      return [...array, ...subColumns];
    }

    const columnKey = column?.accessorKey || column?.key;
    return [...array, columnKey];
  }, []) as string[];

  const newItems = data.reduce(
    (prev, curr) => {
      // Sentinel rows must pass through intact so InnerTable can read all fields
      if (curr?.__loadMoreSentinel) {
        return {
          values: [...prev.values, {}],
          restValues: [
            ...prev.restValues,
            { __loadMoreSentinel: curr.__loadMoreSentinel },
          ],
        };
      }

      const keys = union(Object.keys(curr), columnsKeys) as string[];
      const newItem = keys.reduce(
        (previous, current) => {
          const cell = curr[current];

          if (!cell) {
            return {
              value: { ...previous.value, [current]: '-' },
              restValue: {
                ...previous.restValue,
                [current]: { ...defaultCellOptions },
              },
            };
          }

          const column = columns.find(
            (column) => column?.accessorKey === current,
          );

          const customValue = column?.attribute || 'value';

          const { [customValue]: value, ...rest } = cell;

          return {
            value: { ...previous.value, [current]: value || '-' },
            restValue: { ...previous.restValue, [current]: { ...rest } },
            keyValues: { ...previous.restValue, [current]: { ...rest } },
          };
        },
        { value: {}, restValue: {} },
      );

      return {
        values: [...prev.values, { ...newItem.value }],
        restValues: [...prev.restValues, { ...newItem.restValue }],
      };
    },
    {
      values: [],
      restValues: [],
    },
  );

  return newItems;
}

function insertColumnsAfter(
  columns: any[],
  targetKey: string,
  newColumns: any[],
): any[] {
  const targetIndex = columns.findIndex(
    (column) => column.key === targetKey || column.column === targetKey,
  );

  if (targetIndex === -1) {
    throw new Error(`Column with key "${targetKey}" not found.`);
  }

  const updatedColumns = [
    ...columns?.filter((column) => !column.expander)?.slice(0, targetIndex + 1),
    ...newColumns.map((newCol) => ({
      ...newCol.column_info,
    })),
    ...columns.slice(targetIndex + 1),
  ];

  return updatedColumns;
}

export function createGroupedHeaderAfterHeader({
  columns,
  targetKey,
  newColumns,
  tableViews = [],
}: {
  columns: unknown[];
  targetKey: string;
  newColumns: unknown[];
  tableViews: TabView[];
}): unknown[] {
  const targetIndex = columns.findIndex(
    (column) => column?.key === targetKey || column?.column === targetKey,
  );

  if (targetIndex === -1) {
    throw new Error(`Column with key "${targetKey}" not found.`);
  }

  const newGroupedHeader = {
    header: '',
    key: 'header_grouping_tabviews',
    type: 'group',
    textAlign: 'center',
    columnHeaderBackgroundColor: 'transparent',
    borderColor: 'transparent',
    sortable: false,
    columns: orderBy(
      [
        ...columns
          ?.filter((column) => !column?.expander)
          ?.slice(0, targetIndex + 1),
        ...newColumns.map((newCol) => ({
          ...newCol?.column_info,
        })),
      ],
      (column) => {
        const tabView = tableViews.find((tabView) => {
          return tabView.column === column.key;
        });

        return tabView?.hierarchyOrder || 0;
      },
      'asc',
    ),
  };

  const updatedColumns = [newGroupedHeader, ...columns.slice(targetIndex + 1)];

  return updatedColumns;
}

function insertRowsAfter(
  rows: any[],
  targetKey: string,
  newRows: any[],
  value: string,
  uniqueId: string = "",
): any[] {

  const targetIndex = rows.findIndex(
    (row) =>
      row?.[targetKey]?.value === value &&
      row?.[targetKey]?.uniqueId === uniqueId,
  );

  if (targetIndex === -1) {
    throw new Error(`Row with key "${value}" not found.`);
  }

  const updatedColumns = [
    ...rows?.filter((row) => !row.expander)?.slice(0, targetIndex),
    ...newRows.map((newRow) => ({
      ...newRow,
    })),
    ...rows.slice(targetIndex + 1),
  ];

  return updatedColumns;
}

function removeRowsByKeyAfter(
  rows: Row[],
  oldValues?: { items?: Row[]; originalRow?: Row[]; value: string; expandId?: string },
): Row[] {
  if (!oldValues || !oldValues.items || !oldValues.originalRow) {
    return rows;
  }

  const targetIndex = rows.findIndex((row) =>
    Object.entries(row).some(([key, value]) => {
      return (
        value?.uniqueId?.includes(oldValues.value) ||
        value.value === oldValues.value
      );
    }),
  );

  if (targetIndex === -1) {
    return rows;
  }

  const { expandId } = oldValues;

  const filteredRows = rows.filter((row) => {
    if (expandId && (row as any).__loadMoreSentinel?.expandId === expandId) {
      return false;
    }

    const isNotInOldValues = !oldValues.items?.some(
      (item) => JSON.stringify(item) === JSON.stringify(row),
    );
    const isNotUniqueId = Object.entries(row).every(([key, value]) => {
      return !value?.uniqueId?.includes(oldValues.value);
    });

    return isNotInOldValues && isNotUniqueId;
  });

  const newRows = [...filteredRows];

  newRows.splice(targetIndex, 0, ...oldValues.originalRow);

  return newRows;
}

function getOriginalRow(rows: any[], targetKey: string, value: string): any[] {
  const targetIndex = rows.findIndex(
    (row) => row?.[targetKey]?.value === value,
  );

  if (targetIndex === -1) {
    throw new Error(`Row with key "${value}" not found.`);
  }

  const updatedColumns = [
    ...rows
      ?.filter((row) => !row.expander)
      ?.slice(targetIndex, targetIndex + 1),
  ];

  return updatedColumns;
}

export {
  convertColumns,
  convertItems,
  insertColumnsAfter,
  insertRowsAfter,
  getOriginalRow,
  removeRowsByKeyAfter,
};
